import React, { useState, useRef, useMemo } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { formatCurrency } from '../../utils/formatters';
import { computeSpreadPreview } from '../../utils/creditSpreadPreview';

// creditspread-c5d09f13 -- Seam 15: apply an EXISTING account credit across the
// customer's open invoices oldest-first. The read-only sibling of Record Payment's
// "apply to open invoices" flow: there is no amount to type (the credit's remaining
// balance is the amount) and no credit to create -- it just previews the spread and,
// on confirm, calls spreadExistingCreditAcrossInvoices. The preview reuses the SAME
// computeSpreadPreview helper as Record Payment, so the two surfaces cannot drift.
// Confirm is disabled when there are no open invoices (nothing to apply to). The
// success banner reports the SERVER's actual outcome, never the client estimate.

const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

const SpreadCreditModal = ({ isOpen, onClose, credit, invoices = [], onSuccess }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(false);
    const [successDetail, setSuccessDetail] = useState(null);

    // Synchronous double-click guard -- a ref write is immediate where React state
    // lags a rapid second click. The server is idempotent per (credit, invoice) too,
    // so this is purely to avoid a duplicate in-flight request and a doubled toast.
    const submittingRef = useRef(false);

    const remaining = round2(credit?.availableBalance);

    // Client-side PREVIEW of the oldest-first spread of this credit's remaining
    // balance. Best-effort guidance only: the server recomputes each invoice's
    // authoritative balance inside its own transaction at apply time, so this shows
    // what WILL happen, not a promise.
    const preview = useMemo(() => {
        if (!(remaining > 0)) return { rows: [], leftover: 0, totalApplied: 0 };
        return computeSpreadPreview(invoices, remaining);
    }, [remaining, invoices]);

    if (!isOpen || !credit) return null;

    const hasOpenInvoices = preview.rows.length > 0;

    const handleClose = () => {
        if (submittingRef.current) return; // never close mid-flight
        setFormError(null);
        setFormSuccess(false);
        setSuccessDetail(null);
        onClose();
    };

    const handleConfirm = async () => {
        if (submittingRef.current) return; // reject a rapid second click
        if (!hasOpenInvoices) return;

        submittingRef.current = true;
        setIsSubmitting(true);
        setFormError(null);

        try {
            const fn = httpsCallable(functions, 'spreadExistingCreditAcrossInvoices');
            const res = await fn({ creditId: credit.id });
            const data = res?.data || {};
            setSuccessDetail({
                appliedCount: Number.isFinite(data.appliedCount)
                    ? data.appliedCount
                    : (Array.isArray(data.applied) ? data.applied.length : 0),
                remaining: round2(data.remainingCredit),
            });
            setFormSuccess(true);
            setTimeout(() => {
                if (onSuccess) onSuccess();
                setFormSuccess(false);
                setSuccessDetail(null);
                onClose();
            }, 1800);
        } catch (error) {
            console.error(error);
            // HttpsError.message (e.g. the server's "credit is depleted" precondition)
            // reaches the user verbatim.
            setFormError(error?.message || 'Failed to apply credit. Please try again.');
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800">Apply Credit to Invoices</h2>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        Apply this credit's remaining balance of{' '}
                        <span className="font-semibold text-gray-800">{formatCurrency(remaining)}</span>{' '}
                        to open invoices, oldest first.
                    </p>

                    {/* Affected-invoices preview, mirroring the Record Payment style. Estimate
                        only; the server is authoritative. */}
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm">
                        {!hasOpenInvoices ? (
                            <p className="text-gray-700">
                                This customer has no open invoices to apply this credit to.
                            </p>
                        ) : (
                            <>
                                <p className="font-medium text-purple-900 mb-2">
                                    This will be applied to {preview.rows.length} invoice{preview.rows.length === 1 ? '' : 's'}:
                                </p>
                                <ul className="space-y-1 max-h-48 overflow-y-auto pr-1">
                                    {preview.rows.map((r) => (
                                        <li key={r.id} className="flex justify-between items-center gap-2">
                                            <span className="text-gray-700">Invoice #{r.number}</span>
                                            <span className="tabular-nums text-gray-800">
                                                {formatCurrency(r.apply)}
                                                <span className={`ml-2 text-xs ${r.clears ? 'text-green-600' : 'text-amber-600'}`}>
                                                    {r.clears ? 'clears' : 'partial'}
                                                </span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-2 pt-2 border-t border-purple-200 text-gray-700">
                                    <span className="font-semibold">{formatCurrency(preview.totalApplied)}</span>{' '}
                                    across {preview.rows.length} invoice{preview.rows.length === 1 ? '' : 's'}
                                    {preview.leftover > 0.01 && (
                                        <>; <span className="font-semibold">{formatCurrency(preview.leftover)}</span> left as account credit</>
                                    )}
                                    <span className="block text-xs text-gray-500 mt-0.5">Estimate — final amounts are confirmed when you apply.</span>
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSubmitting || formSuccess || !hasOpenInvoices}
                        className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                    >
                        {isSubmitting ? <Loader2 className="animate-spin" /> : 'Apply to open invoices'}
                    </button>

                    {formSuccess && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                            <CheckCircle size={16} className="shrink-0" />
                            {successDetail
                                ? `Credit applied. ${successDetail.appliedCount} invoice${successDetail.appliedCount === 1 ? '' : 's'} updated${successDetail.remaining > 0.01 ? `; ${formatCurrency(successDetail.remaining)} left as account credit` : ''}.`
                                : 'Credit applied.'}
                        </div>
                    )}

                    {formError && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <AlertTriangle size={16} className="shrink-0" />
                            {formError}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SpreadCreditModal;
